import { UnstyledButton, UnstyledButtonProps } from "@mantine/core";
import { modals } from "@mantine/modals";
import { ForwardedRef, PropsWithChildren, forwardRef } from "react";
import { ObjectSchema } from "yup";

type TModalName = Parameters<typeof modals.openContextModal>[0]["modal"];
type TModalSize = Parameters<typeof modals.openContextModal>[0]["size"];
type EditItemButtonProperties<T extends object> = PropsWithChildren<
  UnstyledButtonProps & {
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
    item?: T;
    schema: ObjectSchema<T>;
    modal: TModalName;
    modalSize?: TModalSize;
  }
>;

/**
 * A button that opens a modal to edit an item.
 * @param root0 - The properties of the button.
 * @param root0.item - The item to be edited.
 * @param root0.modal - The modal to be opened.
 * @param root0.schema - The schema of the item.
 * @param root0.children - The children of the button.
 * @param root0.modalSize - The size of the modal.
 * @param reference - The reference to the button.
 * @returns The element to be rendered.
 */
function EditItemButton<T extends object>(
  { item, modal, schema, children, modalSize, ...properties }: EditItemButtonProperties<T>,
  reference: ForwardedRef<HTMLButtonElement>
) {
  /**
   * Handles the click event on the button.
   * @param event - The event that triggered the click.
   */
  function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
    const defaultValues = schema.getDefault() as T;
    event.preventDefault();

    if (properties.onClick) {
      properties.onClick(event);
    }

    modals.openContextModal({
      modal,
      size: modalSize ?? "xl",
      closeOnClickOutside: false,
      closeOnEscape: false,
      innerProps: {
        item: {
          ...defaultValues,
          ...item
        }
      },
      ml: "110px"
    });
  }

  return (
    <UnstyledButton ref={reference} {...properties} onClick={handleClick}>
      {children}
    </UnstyledButton>
  );
}

const EditItemButtonWithReference = forwardRef(EditItemButton);

EditItemButtonWithReference.displayName = "EditItemButton";

// export default createPolymorphicComponent(EditItemButtonWithReference);
export default EditItemButtonWithReference;
